import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';

export type ColumnAlign = 'left' | 'center' | 'right';

export interface ColumnDef<T> {
  key: string;
  header: string;
  accessor: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number;
  align?: ColumnAlign;
}

export interface FilterDef {
  key: string;
  label: string;
  type: 'text' | 'select';
  placeholder?: string;
  options?: { label: string; value: string }[];
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string;
  filters?: FilterDef[];
  selectable?: boolean;
  total?: number;
  initialPageSize?: number;
  emptyMessage?: string;
}

const alignClass: Record<ColumnAlign, string> = {
  left: 'text-start',
  center: 'text-center',
  right: 'text-end',
};

function matchesFilter<T>(row: T, filter: FilterDef, value: string): boolean {
  if (!value) return true;
  const record = row as Record<string, unknown>;
  const v = value.toLowerCase();

  if (filter.key === 'name') {
    const parts = [record['name_en'], record['name_ar'], record['slug'], record['description_en']]
      .filter(Boolean)
      .map(String)
      .join(' ')
      .toLowerCase();
    return parts.includes(v);
  }

  if (filter.key === 'status') {
    const active =
      record['is_active'] === true ||
      record['status'] === 'active' ||
      String(record['status']).toLowerCase() === 'active';
    if (value === 'active') return active;
    if (value === 'inactive') return !active;
    return (
      String(record['status'] ?? '').toLowerCase() === v ||
      String(record['status'] ?? '')
        .toLowerCase()
        .includes(v)
    );
  }

  const field = record[filter.key];
  if (field == null) return false;
  return String(field).toLowerCase() === v || String(field).toLowerCase().includes(v);
}

export function DataTable<T>({
  data,
  columns,
  rowKey,
  filters = [],
  selectable = false,
  total,
  initialPageSize = 10,
  emptyMessage,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage ?? t('dataTable.noResults');

  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    return data.filter((row) =>
      filters.every((f) => matchesFilter(row, f, filterValues[f.key] ?? '')),
    );
  }, [data, filters, filterValues]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir, columns]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const displayTotal = total ?? sorted.length;
  const rangeFrom = sorted.length === 0 ? 0 : safePage * pageSize + 1;
  const rangeTo = Math.min((safePage + 1) * pageSize, sorted.length);

  const toggleSort = (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col?.sortValue) return;
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllPage = () => {
    const ids = pageRows.map(rowKey);
    const allSelected = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4 overflow-hidden rounded-2xl border border-border bg-card shadow-md">
      {filters.length > 0 ? (
        <div className="flex flex-wrap gap-3 border-b border-border p-4">
          {filters.map((f) =>
            f.type === 'text' ? (
              <Input
                key={f.key}
                placeholder={f.placeholder ?? f.label}
                value={filterValues[f.key] ?? ''}
                onChange={(e) => {
                  setFilterValues((prev) => ({ ...prev, [f.key]: e.target.value }));
                  setPage(0);
                }}
                className="h-9 max-w-xs"
              />
            ) : (
              <select
                key={f.key}
                aria-label={f.label}
                value={filterValues[f.key] ?? ''}
                onChange={(e) => {
                  setFilterValues((prev) => ({ ...prev, [f.key]: e.target.value }));
                  setPage(0);
                }}
                className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
              >
                <option value="">{f.label}</option>
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ),
          )}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {selectable ? (
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    aria-label={t('dataTable.selectAll')}
                    checked={pageRows.length > 0 && pageRows.every((r) => selected.has(rowKey(r)))}
                    onChange={toggleAllPage}
                  />
                </th>
              ) : null}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-4 py-3 font-medium text-muted-foreground',
                    alignClass[col.align ?? 'left'],
                    col.sortValue && 'cursor-pointer select-none hover:text-foreground',
                  )}
                  onClick={() => toggleSort(col.key)}
                >
                  {col.header}
                  {sortKey === col.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center text-muted-foreground"
                >
                  {resolvedEmptyMessage}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => {
                const id = rowKey(row);
                return (
                  <tr key={id} className="border-b border-border/60 hover:bg-muted/20">
                    {selectable ? (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label={t('dataTable.selectRow')}
                          checked={selected.has(id)}
                          onChange={() => toggleRow(id)}
                        />
                      </td>
                    ) : null}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn('px-4 py-3', alignClass[col.align ?? 'left'])}
                      >
                        {col.accessor(row)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-xs text-muted-foreground">
        <span>
          {t('dataTable.showing', { from: rangeFrom, to: rangeTo, total: displayTotal })}
          {selected.size > 0 ? ` · ${t('dataTable.selected', { count: selected.size })}` : ''}
        </span>
        <div className="flex items-center gap-2">
          <select
            aria-label={t('dataTable.rowsPerPageAria')}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            className="h-8 rounded-md border border-border bg-background px-2"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {t('dataTable.rowsPerPage', { count: n })}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            disabled={safePage <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            {t('dataTable.previous')}
          </Button>
          <span className="tabular-nums">
            {t('dataTable.pageOf', { current: safePage + 1, total: pageCount })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            {t('dataTable.next')}
          </Button>
        </div>
      </div>
    </div>
  );
}
